import React from "react";

import {
    intl, DmsAltBox, DmsPage, DmsTabber, havePermissions
} from "dmsbasereact";

import { Typography } from "@mui/material";
import { GeneralLogsSettings } from "../GeneralLogsSettings/GeneralLogsSettings";
import { AuthLoginSettings } from "../AuthLoginSettings/AuthLoginSettings";
import { SecuritySettings } from "../SecuritySettings/SecuritySettings";
import { CommunicationSettings } from "../CommunicationSettings/CommunicationSettings";
import { IntegrationsSettings } from "../IntegrationsSettings/IntegrationsSettings";
import { DebugSettings } from "../DebugSettings/DebugSettings";
import { NoticeSettings } from "../NoticeSettings/NoticeSettings";

import { Permissions } from "../../constants/Permissions";

/**
 * SystemSettings - Domain-based tabs for system configuration.
 *
 * Tab Structure (reorganized from 12 to 6 tabs):
 * 1. General - Language, keyboard settings + Log retention
 * 2. Authentication - SSO/LDAP + Password policy, session settings
 * 3. Security - Rate Limiter & MFA Protection
 * 4. Communication - SMTP settings (System + Alert)
 * 5. Integrations - CFM API + SAP + API Keys
 * 6. Debug - Developer tools (only for Accenture role)
 *
 * Note: Content settings (Help Center + Documents) moved to separate page in sidebar
 * Note: Login Settings moved to Authentication > Password & Session tab
 * Note: Tabs are built conditionally based on user permissions (supports OR logic)
 */
export const SystemSettings = () => {
    // Build tabs array based on user permissions
    const tabs = [];

    // General tab - single permission (AND logic)
    if (havePermissions([Permissions.CAN_MANAGE_GENERAL_SETTINGS])) {
        tabs.push({
            title: intl.get("General").d("General"),
            component: <GeneralLogsSettings />
        });
    }

    // Authentication tab - OR logic (any of these permissions)
    if (havePermissions([Permissions.CAN_MANAGE_SSO_SETTINGS, Permissions.CAN_MANAGE_LOGIN_SETTINGS], false)) {
        tabs.push({
            title: intl.get("Authentication").d("Authentication"),
            component: <AuthLoginSettings />
        });
    }

    // Security tab - single permission
    if (havePermissions([Permissions.CAN_MANAGE_SECURITY_SETTINGS])) {
        tabs.push({
            title: intl.get("Security").d("Security"),
            component: <SecuritySettings />
        });
    }

    // Communication tab - single permission
    if (havePermissions([Permissions.CAN_MANAGE_SMTP_SETTINGS])) {
        tabs.push({
            title: intl.get("Communication").d("Communication"),
            component: <CommunicationSettings />
        });
    }

    // Integrations tab - OR logic (any of these permissions)
    if (havePermissions([
        Permissions.CAN_MANAGE_GENERAL_SETTINGS,
        Permissions.CAN_MANAGE_SAP_SETTINGS,
        Permissions.CAN_MANAGE_API_KEYS
    ], false)) {
        tabs.push({
            title: intl.get("Integrations").d("Integrations"),
            component: <IntegrationsSettings />
        });
    }

    // Notices tab - single permission
    if (havePermissions([Permissions.NOTICE_MANAGE])) {
        tabs.push({
            title: intl.get("Notices").d("Notices"),
            component: <NoticeSettings />
        });
    }

    // Debug tab - single permission
    if (havePermissions([Permissions.SETTINGS_DEBUG])) {
        tabs.push({
            title: intl.get("Debug").d("Debug"),
            component: <DebugSettings />
        });
    }

    // Edge case: No tabs visible (shouldn't happen if route permission is correct)
    if (tabs.length === 0) {
        return (
            <DmsPage name={intl.get("SystemSettings").d("System Settings")} manual_slug="system-settings">
                <DmsAltBox.Card>
                    <DmsAltBox.Header>
                        <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                            {intl.get("SystemSettings").d("System Settings")}
                        </Typography>
                    </DmsAltBox.Header>
                    <DmsAltBox.Body>
                        <Typography>
                            {intl.get("NoSettingsPermissions").d("You do not have permission to view any settings tabs.")}
                        </Typography>
                    </DmsAltBox.Body>
                </DmsAltBox.Card>
            </DmsPage>
        );
    }

    return (
        <DmsPage name="System Settings" manual_slug="system-settings">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                        {intl.get("SystemSettings").d("System Settings")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTabber tabs={tabs} pageName={intl.get("SystemSettings").d("System Settings")} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};
